import { APIClient } from '../APIClient'
import PublicGlobals from '../PublicGlobals'

// Client for the backend's named, multi-conversation "chat about contracts" API
// (NestJS, mounted at /chat/conversations). Mirrors the backend DTOs.

/** An ApiProvider slug (Settings → API Providers). */
export type ChatProvider = string

export interface ChatConversation {
  id: number
  title: string
  documentIds: number[]
  profileId: number | null
  lensId: number | null
  includeAllDocuments: boolean
  includeUserChatHistory: boolean
  useRawText: boolean
  numberOfChunks: number
  apiProvider: string | null
  model: string | null
  /** Set when this is the per-document sidebar conversation; null otherwise. */
  documentId?: number | null
  createdAt: string
  updatedAt: string
}

/** Per-conversation chat settings. */
export interface ChatSettings {
  profileId?: number | null
  lensId?: number | null
  includeAllDocuments?: boolean
  includeUserChatHistory?: boolean
  useRawText?: boolean
  numberOfChunks?: number
  apiProvider?: string
  model?: string | null
  /** Selected curated collection; backend grounds on its contract documents. */
  collectionId?: number | null
}

export interface ChatMessage {
  id: number
  role: 'user' | 'assistant'
  content: string
  createdAt: string
}

export interface Paginated<T> {
  data: T[]
  total: number
  page: number
  limit: number
  totalPages: number
}

/** Read-only payload returned by the public/private shared-conversation endpoints. */
export interface SharedMessages extends Paginated<ChatMessage> {
  title: string
}

/** Normalize document ids (search results use string ids) to integers. */
const toIntIds = (ids: (string | number)[] = []): number[] =>
  ids.map((id) => Number(id)).filter((n) => Number.isFinite(n))

/** Build the request body for the settings fields that are actually set. */
const settingsBody = (s: ChatSettings): Record<string, unknown> => ({
  ...(s.profileId !== undefined && { profileId: s.profileId }),
  ...(s.lensId !== undefined && { lensId: s.lensId }),
  ...(s.includeAllDocuments !== undefined && { includeAllDocuments: s.includeAllDocuments }),
  ...(s.includeUserChatHistory !== undefined && {
    includeUserChatHistory: s.includeUserChatHistory,
  }),
  ...(s.useRawText !== undefined && { useRawText: s.useRawText }),
  ...(s.numberOfChunks !== undefined && { numberOfChunks: s.numberOfChunks }),
  ...(s.apiProvider !== undefined && { apiProvider: s.apiProvider }),
  ...(s.model !== undefined && { model: s.model }),
})

export const chatConversationService = {
  list(params: { search?: string; page?: number; limit?: number } = {}) {
    return APIClient.makeAPICall<Paginated<ChatConversation>>('GET', '/chat/conversations', {
      queryParams: params,
    })
  },

  create(
    input: { title?: string; documentIds?: (string | number)[] } & ChatSettings = {}
  ) {
    return APIClient.makeAPICall<ChatConversation>('POST', '/chat/conversations', {
      body: {
        title: input.title,
        documentIds: toIntIds(input.documentIds),
        ...settingsBody(input),
      },
    })
  },

  get(id: number) {
    return APIClient.makeAPICall<ChatConversation>('GET', `/chat/conversations/${id}`)
  },

  /**
   * Resolve (or lazily create) the caller's sidebar conversation for a document.
   * The document drawer and "open in full chat" share this one conversation, so
   * the thread is continuable from either place. Legacy per-document chat history
   * is backfilled into it on first creation.
   */
  forDocument(documentId: string | number) {
    return APIClient.makeAPICall<ChatConversation>(
      'GET',
      `/chat/conversations/for-document/${Number(documentId)}`
    )
  },

  update(
    id: number,
    input: { title?: string; documentIds?: (string | number)[] } & ChatSettings
  ) {
    return APIClient.makeAPICall<ChatConversation>('PATCH', `/chat/conversations/${id}`, {
      body: {
        ...(input.title != null && { title: input.title }),
        ...(input.documentIds != null && { documentIds: toIntIds(input.documentIds) }),
        ...settingsBody(input),
      },
    })
  },

  remove(id: number) {
    return APIClient.makeAPICall<{ success: boolean }>('DELETE', `/chat/conversations/${id}`)
  },

  getMessages(id: number, params: { page?: number; limit?: number } = {}) {
    return APIClient.makeAPICall<Paginated<ChatMessage>>(
      'GET',
      `/chat/conversations/${id}/messages`,
      { queryParams: params }
    )
  },

  /** Plain-text (Markdown) AI summary of a conversation (for the summary modal). */
  getSummary(id: number) {
    return APIClient.makeAPICall<{ summary: string }>(
      'GET',
      `/chat/conversations/${id}/summary`
    )
  },

  /** Create a public (anyone-with-link) share URL for a conversation. */
  createShareLink(id: number) {
    return APIClient.makeAPICall<{ shareUrl: string }>(
      'POST',
      `/chat/share/conversation/${id}`,
      { body: {} }
    )
  },

  /** Create a private, email-gated share URL for a conversation. */
  createPrivateShareLink(id: number, email: string) {
    return APIClient.makeAPICall<{ shareUrl: string }>(
      'POST',
      '/chat/share/private/conversation',
      { body: { conversationId: id, email } }
    )
  },

  /** Public viewer: fetch messages of a shared conversation (no auth header). */
  getSharedMessages(token: string, params: { page?: number; limit?: number } = {}) {
    return APIClient.makeAPICall<SharedMessages>(
      'GET',
      `/chat/shared/conversation/${token}/messages`,
      { queryParams: params, noAuthorizationHeader: true }
    )
  },

  /** Private viewer: fetch messages of an email-gated shared conversation (auth). */
  getPrivateSharedMessages(token: string, params: { page?: number; limit?: number } = {}) {
    return APIClient.makeAPICall<SharedMessages>(
      'GET',
      `/chat/shared/private/conversation/${token}/messages`,
      { queryParams: params }
    )
  },

  /**
   * Send a message and stream the grounded reply. `documentIds`, when provided,
   * replaces the conversation's attachments before answering (attach mid-chat).
   */
  streamMessage(
    id: number,
    message: string,
    opts: {
      documentIds?: (string | number)[]
      onDelta?: (text: string) => void
      onDone?: () => void
      signal?: AbortSignal
    } & ChatSettings = {}
  ) {
    return APIClient.streamSSE('POST', `/chat/conversations/${id}/stream`, {
      body: {
        message,
        ...(opts.documentIds != null && { documentIds: toIntIds(opts.documentIds) }),
        ...(opts.collectionId != null && { collectionId: Number(opts.collectionId) }),
        ...settingsBody(opts),
      },
      onDelta: opts.onDelta,
      onDone: opts.onDone,
      signal: opts.signal,
    })
  },
}

export default chatConversationService

/**
 * Full URL for a conversation's spoken-summary (Chirp 3 HD MP3) endpoint. The
 * `<audio>` element can't send the auth header, so callers fetch this as a blob
 * with explicit auth (see fetchBlobUrl) and feed the object URL to the element.
 */
export function conversationSummaryAudioChirpUrl(id: number): string {
  const base = (PublicGlobals.API_BASE_URL || 'http://localhost:3001/v1').replace(
    /\/$/,
    ''
  )
  return `${base}/chat/conversations/${id}/summary-audio-chirp`
}
